import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from 'react-use';

import useCameraForecast from 'hooks/useCameraForecast';
import type { CamForecastComparison, CamForecastTopMatch } from 'types/camera';

import { CamMatches } from './CamMatches';
import { pickClosestForecastComparison } from './pickClosestForecastComparison';
import {
  CAM_MATCHES_EXPANDED_STORAGE_KEY,
  collectCamMatchGridPrimaryUrls,
  collectCamMatchGridPrimaryUrlsForDays,
  prefetchCamMatchGridUrls,
} from './prefetchCamMatchThumbnails';

const TIMESTAMP_DEBOUNCE_MS = 50;

export interface CamMatchesContainerProps {
  spotId: string;
  days: number;
  timestamp?: number | null;
  contentLeft?: number;
  contentWidth?: number;
  visibleDayIndices?: readonly number[];
}

export const CamMatchesContainer: React.FC<CamMatchesContainerProps> = ({
  spotId,
  days,
  timestamp,
  contentLeft,
  contentWidth,
  visibleDayIndices,
}) => {
  const [isExpandedStorage, setIsExpandedStorage] = useLocalStorage(
    CAM_MATCHES_EXPANDED_STORAGE_KEY,
    true,
  );
  const isExpanded = isExpandedStorage ?? true;
  const onToggleExpanded = useCallback(
    () => setIsExpandedStorage(!isExpanded),
    [isExpanded, setIsExpandedStorage],
  );

  const { data, error, isLoading } = useCameraForecast({ spotId, days, enabled: isExpanded });

  const [debouncedTimestamp, setDebouncedTimestamp] = useState(timestamp);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedTimestamp(timestamp), TIMESTAMP_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [timestamp]);

  // Track which days have been preloaded so we never re-fetch the same day.
  const preloadedDaysRef = useRef<Set<number>>(new Set<number>());
  useEffect(() => {
    if (!data) return;
    if (visibleDayIndices !== undefined) {
      const newDays = visibleDayIndices.filter((d) => !preloadedDaysRef.current.has(d));
      if (!newDays.length) return;
      prefetchCamMatchGridUrls(collectCamMatchGridPrimaryUrlsForDays(data, new Set(newDays)));
      newDays.forEach((d) => preloadedDaysRef.current.add(d));
    } else {
      if (preloadedDaysRef.current.has(-1)) return;
      prefetchCamMatchGridUrls(collectCamMatchGridPrimaryUrls(data));
      preloadedDaysRef.current.add(-1);
    }
  }, [data, visibleDayIndices]);

  const { matches, forecastComparison } = useMemo((): {
    matches: readonly CamForecastTopMatch[] | undefined;
    forecastComparison: Pick<CamForecastComparison, 'datetime' | 'utcOffset'> | null;
  } => {
    if (isLoading || error || !data) {
      return { matches: undefined, forecastComparison: null };
    }
    const comparisons = data.futureCam?.forecastComparisons;
    if (!comparisons?.length) {
      return { matches: [], forecastComparison: null };
    }
    return pickClosestForecastComparison(comparisons, debouncedTimestamp);
  }, [data, debouncedTimestamp, error, isLoading]);

  return (
    <CamMatches
      spotId={spotId}
      loading={isLoading}
      error={Boolean(error)}
      matches={matches}
      forecastComparison={forecastComparison}
      contentLeft={contentLeft}
      contentWidth={contentWidth}
      isExpanded={isExpanded}
      onToggleExpanded={onToggleExpanded}
    />
  );
};
